/**
 * The voice-clip wire shape: 16kHz mono PCM, µ-law companded, base64.
 *
 * Deliberately not MediaRecorder. The OS recorders do not round-trip across
 * platforms — Android records webm/opus, which an iPhone's decodeAudioData
 * cannot open — so a mixed table would hear only some of its members. Raw
 * samples companded the way telephones have done it since G.711 decode
 * identically everywhere we ship, and phone quality is exactly the register a
 * hold-to-talk button promises.
 */

export const VOICE_FORMAT = 'pcm-mulaw;rate=16000';
export const VOICE_RATE = 16_000;

const BIAS = 0x84;
const CLIP = 32_635;

/**
 * Average the input over each output sample's window. For the ratios that
 * occur here (48000→16000, 44100→16000) the window is a few samples wide,
 * which is enough low-pass to keep speech clean at the target rate.
 */
export function downsample(
  input: Float32Array<ArrayBuffer>,
  from: number,
  to: number = VOICE_RATE,
): Float32Array<ArrayBuffer> {
  if (from <= to) return input;
  const ratio = from / to;
  const out = new Float32Array(Math.floor(input.length / ratio));
  for (let i = 0; i < out.length; i++) {
    const start = Math.floor(i * ratio);
    const end = Math.min(input.length, Math.max(start + 1, Math.floor((i + 1) * ratio)));
    let sum = 0;
    for (let k = start; k < end; k++) sum += input[k] ?? 0;
    out[i] = sum / (end - start);
  }
  return out;
}

/**
 * Below this peak the clip is a quiet room, not a person. Measured on the
 * test phone: a silent room reaches 0.005 raw and 0.0007 once the browser's
 * noise suppression has had it, while any actual talking is an order of
 * magnitude above that even before gain.
 */
const SILENCE_PEAK = 0.02;
/** Where a clip should land, loud enough to hear over a game on a phone. */
const TARGET_PEAK = 0.7;
/** A whisper is worth lifting; a whisper of hiss is not worth a roar. */
const MAX_GAIN = 12;

/**
 * Bring a clip up to a consistent level, or refuse it.
 *
 * The browser's own gain control is tuned for a continuous call, not for a
 * held button, and a quiet talker in a noisy sari-sari store arrives far
 * below what a phone speaker can play over a game. Returning null for what is
 * really just room tone matters as much: an empty clip still crossed the
 * wire, still lit the speaker's pod, and still made everyone wonder what they
 * had missed.
 */
export function normalize(pcm: Float32Array<ArrayBuffer>): Float32Array<ArrayBuffer> | null {
  let peak = 0;
  for (const s of pcm) {
    const v = Math.abs(s);
    if (v > peak) peak = v;
  }
  if (peak < SILENCE_PEAK) return null;
  const gain = Math.min(MAX_GAIN, TARGET_PEAK / peak);
  if (gain <= 1) return pcm;
  const out = new Float32Array(pcm.length);
  for (let i = 0; i < pcm.length; i++) out[i] = Math.max(-1, Math.min(1, (pcm[i] ?? 0) * gain));
  return out;
}

export function mulawEncode(pcm: Float32Array): Uint8Array {
  const out = new Uint8Array(pcm.length);
  for (let i = 0; i < pcm.length; i++) {
    let s = Math.max(-1, Math.min(1, pcm[i] ?? 0)) * 32_767;
    const sign = s < 0 ? 0x80 : 0;
    if (s < 0) s = -s;
    if (s > CLIP) s = CLIP;
    s += BIAS;
    let exp = 7;
    for (let mask = 0x4000; (s & mask) === 0 && exp > 0; exp--, mask >>= 1);
    const mantissa = (s >> (exp + 3)) & 0x0f;
    out[i] = ~(sign | (exp << 4) | mantissa) & 0xff;
  }
  return out;
}

export function mulawDecode(bytes: Uint8Array): Float32Array<ArrayBuffer> {
  const out = new Float32Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) {
    const u = ~(bytes[i] ?? 0) & 0xff;
    const exp = (u >> 4) & 0x07;
    const mantissa = u & 0x0f;
    let s = (((mantissa << 3) + BIAS) << exp) - BIAS;
    if (u & 0x80) s = -s;
    out[i] = s / 32_767;
  }
  return out;
}

/** btoa chokes past a few tens of KB of spread args; chunk it. */
export function toBase64(bytes: Uint8Array): string {
  let binary = '';
  const STEP = 0x8000;
  for (let i = 0; i < bytes.length; i += STEP)
    binary += String.fromCharCode(...bytes.subarray(i, i + STEP));
  return btoa(binary);
}

export function fromBase64(data: string): Uint8Array {
  const binary = atob(data);
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i);
  return out;
}
